import { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	useLoomRecorderEntryPoint,
	type UseLoomRecorderEntryPointProps,
} from '@atlassian/loom-utils';
import {
	ANALYTICS_CHANNEL,
	ANALYTICS_TAG,
	ATI_PRESETS,
	LOOM_ATI,
} from '@atlassian/universal-create-common';

import { SOURCE_LOCATION } from '../../../../../../constants';
import { useMenuControl } from '../../../../../../context';
import type { MenuItem, SuggestionConfigContext } from '../../../../../../types';

type LoomVideoExternalConfig = Pick<
	UseLoomRecorderEntryPointProps,
	'cloudId' | 'productName' | 'environment' | 'skipInitialisation'
> & {
	onItemClick?: SuggestionConfigContext['onItemClick'];
};

type LoomVideoConfig = Pick<MenuItem, 'onClick' | 'isDisabled' | 'isLoading'>;

const messages = defineMessages({
	insertButtonText: {
		id: 'universal-create.loom-video-menu-item.share-video',
		defaultMessage: 'Share',
		description: 'Button text for inserting a Loom video',
	},
});

/**
 * Hook to configure the Loom video menu item
 * @param cloudId - The cloud ID of the Loom video
 * @param productName - The name of the product
 * @param environment - The environment of the product
 * @param skipInitialisation - Whether to skip the initialisation of the Loom SDK
 * @returns The configuration for the Loom video menu item
 */
export function useLoomVideoConfig({
	cloudId,
	productName,
	environment = 'prod',
	onItemClick,
	skipInitialisation = false,
}: LoomVideoExternalConfig): LoomVideoConfig {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isNav4Enabled, product, subProduct } = useMenuControl();

	const attributes = {
		isNav4: isNav4Enabled,
		product,
		subProduct,
	};

	const { openLoomRecorder, recorderState } = useLoomRecorderEntryPoint({
		entryPointLocation: SOURCE_LOCATION,
		environment,
		cloudId,
		productName,
		onInsert: (videoUrl: string) => {
			const url = new URL(videoUrl);
			// Open video in new tab with share permissions modal open
			url.searchParams.set('open_share_permissions', '1');
			window.open(url.toString(), '_blank', 'noopener');
		},
		insertButtonText: formatMessage(messages.insertButtonText),
		eventHandler: {
			onRecordingComplete: () =>
				createAnalyticsEvent({
					action: 'completed',
					actionSubject: 'loomRecording',
					eventType: 'track',
					tags: [ANALYTICS_TAG],
					attributes,
				}).fire(ANALYTICS_CHANNEL),
			onUploadComplete: () =>
				createAnalyticsEvent({
					action: 'uploaded',
					actionSubject: 'loomRecording',
					eventType: 'track',
					tags: [ANALYTICS_TAG],
					attributes,
				}).fire(ANALYTICS_CHANNEL),
			onInsertClicked: () =>
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'insertVideoLink',
					eventType: 'ui',
					tags: [ANALYTICS_TAG],
					attributes,
				}).fire(ANALYTICS_CHANNEL),
		},
		skipInitialisation,
	});

	const onClick = useCallback(() => {
		if (openLoomRecorder) {
			onItemClick?.(fg('navx-2354-uc-refactor-ati-preset') ? ATI_PRESETS.LOOM : LOOM_ATI);
			openLoomRecorder();
		}
	}, [openLoomRecorder, onItemClick]);

	switch (recorderState) {
		case 'initialising':
			return {
				isLoading: true,
			};
		case 'ready':
			if (openLoomRecorder) {
				return {
					onClick,
				};
			}
			return {
				isDisabled: true,
			};
		default:
			return {
				isDisabled: true,
			};
	}
}
